import { getPromoTags } from 'promo-tag-utils';

class CarouselComponent extends HTMLElement {
  constructor() {
    super();
    this.carousel = this.querySelector('[id^="Carousel-"]');
    this.carouselItems = this.querySelectorAll('[id^="Carousel-Item-"]');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.carousel || !this.nextButton) return;

    // Defer until after first paint so layout geometry is available for
    // isElementInViewport to correctly identify off-screen slides on init
    requestAnimationFrame(() => this._initPages());
    const resizeObserver = new ResizeObserver(entries => this._initPages());
    resizeObserver.observe(this.carousel);

    this.carousel.addEventListener('scroll', this._update.bind(this));
    this.prevButton.addEventListener('click', this._onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this._onButtonClick.bind(this));
  }

  _initPages() {
    const carouselItemArray = [...this.carouselItems];
    this.carouselItemsToShow = carouselItemArray.filter(
      element => element.clientWidth > 0
    );

    if (this.carouselItemsToShow.length < 2) return;
    this.carouselItemOffset =
      this.carouselItemsToShow[1].offsetLeft -
      this.carouselItemsToShow[0].offsetLeft;
    this.carouselItemsPerPage = Math.floor(
      (this.carousel.clientWidth - this.carouselItemsToShow[0].offsetLeft) /
        this.carouselItemOffset
    );
    this.totalPages =
      this.carouselItemsToShow.length - this.carouselItemsPerPage + 1;
    this._update();
  }

  _update() {
    // Derive the current 1-based page index from scroll position
    this.currentPage =
      Math.round(this.carousel.scrollLeft / this.carouselItemOffset) + 1;

    // Disable prev button when at the start
    if (this.carousel.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    // Disable next button when at the end; >= handles sub-pixel rounding
    if (
      this.carousel.scrollLeft + this.carousel.offsetWidth >=
      this.carousel.scrollWidth
    ) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }

    // Keep aria-hidden and tabindex in sync with slide visibility
    this._updateSlideAccessibility();
  }

  _updateSlideAccessibility() {
    const FOCUSABLE = 'a, button, input, select, textarea, [tabindex]';
    // Detect carousel mode by checking if all items share the same vertical
    // position. If any item wraps to a new row the layout is a grid, not a
    // carousel — regardless of whether the container still overflows horizontally.
    const firstTop = this.carouselItemsToShow[0]?.offsetTop;
    const lastTop =
      this.carouselItemsToShow[this.carouselItemsToShow.length - 1]?.offsetTop;
    const carouselActive =
      this.carouselItemsToShow.length >= 2 && firstTop === lastTop;

    const total = this.carouselItemsToShow.length;

    this.carouselItemsToShow.forEach((slide, index) => {
      // Check visibility relative to the carousel scroll container so this
      // works correctly when the carousel is inside a drawer or modal that
      // isn't itself fully within the window viewport.
      let visible = true;
      if (carouselActive) {
        const slideLeft = slide.offsetLeft;
        const slideRight = slideLeft + slide.clientWidth;
        const viewLeft = this.carousel.scrollLeft;
        const viewRight = viewLeft + this.carousel.clientWidth;
        const overlap =
          Math.min(slideRight, viewRight) - Math.max(slideLeft, viewLeft);
        visible = overlap / slide.clientWidth >= 0.5;
      }

      // "slide X of Y" label is only meaningful in carousel context
      if (carouselActive) {
        slide.setAttribute('aria-label', `slide ${index + 1} of ${total}`);
      } else {
        slide.removeAttribute('aria-label');
      }

      if (visible) {
        slide.removeAttribute('aria-hidden');
        slide
          .querySelectorAll(FOCUSABLE)
          .forEach(el => el.removeAttribute('tabindex'));
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide
          .querySelectorAll(FOCUSABLE)
          .forEach(el => el.setAttribute('tabindex', '-1'));
      }
    });
  }

  _onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.carousel.scrollLeft + step * this.carouselItemOffset
        : this.carousel.scrollLeft - step * this.carouselItemOffset;
    this.carousel.scrollTo({
      left: this.slideScrollPosition,
      behavior: 'smooth'
    });
  }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', CarouselComponent);
}

let productCardGalleryId = 0;

class ProductCard extends HTMLElement {
  // Specify observed attributes so that attributeChangedCallback fires
  static get observedAttributes() {
    return ['product', 'disable-image-carousel'];
  }

  constructor() {
    super();
    this._product = null;
    this._galleryBuilt = false;
    this._galleryMediaQuery = window.matchMedia('(max-width: 767px)');
    this._handleGalleryMediaChange = () => this._syncGallery();
  }

  connectedCallback() {
    this._galleryMediaQuery.addEventListener(
      'change',
      this._handleGalleryMediaChange
    );
    this._syncGallery();
  }

  disconnectedCallback() {
    this._galleryMediaQuery.removeEventListener(
      'change',
      this._handleGalleryMediaChange
    );
    this._resizeObserver?.disconnect();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    if (oldVal === newVal) return;

    if (attr === 'disable-image-carousel') {
      this._syncGallery();
      return;
    }

    if (attr !== 'product') return;
    if (newVal === 'null' || newVal === 'undefined') return;

    this._product = JSON.parse(newVal);
    if (this._product?.tags) getPromoTags();
    this._syncGallery();
  }

  _syncGallery() {
    if (
      this._galleryMediaQuery.matches &&
      !this.hasAttribute('disable-image-carousel')
    ) {
      this._buildGallery();
    } else {
      this._destroyGallery();
    }
  }

  _buildGallery() {
    const images = this._product?.images;
    if (this._galleryBuilt || !Array.isArray(images) || images.length < 2) {
      return;
    }

    const slides = this.querySelector('.product-card__image');
    const firstImage = slides?.querySelector('img');
    if (!slides || !firstImage || !images[1]) return;
    this._galleryBuilt = true;

    const secondImage = document.createElement('img');
    secondImage.className = 'product-card__gallery-image';
    secondImage.alt = '';
    secondImage.loading = 'lazy';
    secondImage.decoding = 'async';
    secondImage.src = images[1];

    firstImage.classList.add('product-card__gallery-image');
    slides.classList.add('product-card__gallery-slides');
    slides.appendChild(secondImage);
    this._generatedSlidesId = !slides.id;
    if (this._generatedSlidesId) {
      slides.id = `ProductCardGallery-${++productCardGalleryId}`;
    }

    const scrollbar = document.createElement('div');
    scrollbar.className = 'product-card__gallery-scrollbar';
    scrollbar.tabIndex = 0;
    scrollbar.setAttribute('role', 'scrollbar');
    scrollbar.setAttribute('aria-label', 'Product images');
    scrollbar.setAttribute('aria-controls', slides.id);
    scrollbar.setAttribute('aria-orientation', 'horizontal');
    scrollbar.setAttribute('aria-valuemin', '1');
    scrollbar.setAttribute('aria-valuemax', '2');
    scrollbar.setAttribute('aria-valuenow', '1');
    scrollbar.setAttribute('aria-valuetext', 'Image 1 of 2');

    const thumb = document.createElement('div');
    thumb.className = 'product-card__gallery-scrollbar-thumb';
    scrollbar.appendChild(thumb);

    const gallery = document.createElement('div');
    gallery.className = 'product-card__gallery';
    slides.before(gallery);
    gallery.append(slides);
    gallery.after(scrollbar);

    this._slides = slides;
    this._scrollbar = scrollbar;
    this._thumb = thumb;
    this._galleryAbortController = new AbortController();
    const { signal } = this._galleryAbortController;

    slides.addEventListener('scroll', () => this._updateScrollbar(), {
      passive: true,
      signal
    });
    slides.addEventListener('dragstart', event => event.preventDefault(), {
      signal
    });

    // Mouse drag-to-scroll on the images. Native touch/trackpad scrolling and
    // scroll-snap are handled by CSS, so JS only adds pointer dragging.
    const dragged = this._enableDrag(slides, {
      threshold: 5,
      onStart: () => (this._startScroll = slides.scrollLeft),
      onMove: dx => {
        slides.classList.add('is-dragging');
        slides.scrollLeft = this._startScroll - dx;
      },
      onEnd: () => {
        slides.classList.remove('is-dragging');
        this._snapToClosest();
      },
      signal
    });
    // Suppress the link navigation when the pointer was dragged.
    slides.addEventListener(
      'click',
      event => {
        if (dragged()) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      { signal }
    );

    // Drag the scrollbar thumb.
    this._enableDrag(thumb, {
      onStart: () => (this._startScroll = slides.scrollLeft),
      onMove: dx => {
        const travel = scrollbar.clientWidth - thumb.offsetWidth;
        if (travel > 0) {
          slides.scrollLeft = this._startScroll + (dx / travel) * this._range();
        }
      },
      onEnd: () => this._snapToClosest(),
      signal
    });

    // Click anywhere on the track (outside the thumb) to jump.
    scrollbar.addEventListener(
      'pointerdown',
      event => {
        if (event.target === thumb) return;
        const bounds = scrollbar.getBoundingClientRect();
        const progress = Math.min(
          Math.max((event.clientX - bounds.left) / bounds.width, 0),
          1
        );
        slides.scrollTo({ left: progress * this._range(), behavior: 'smooth' });
      },
      { signal }
    );

    // Keyboard support for the scrollbar (2 images: start or end).
    scrollbar.addEventListener(
      'keydown',
      event => {
        const forward = event.key === 'ArrowRight' || event.key === 'End';
        const backward = event.key === 'ArrowLeft' || event.key === 'Home';
        if (!forward && !backward) return;
        event.preventDefault();
        slides.scrollTo({
          left: forward ? slides.clientWidth : 0,
          behavior: 'smooth'
        });
      },
      { signal }
    );

    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this._updateScrollbar());
      this._resizeObserver.observe(slides);
    }
    requestAnimationFrame(() => this._updateScrollbar());
  }

  _destroyGallery() {
    if (!this._galleryBuilt) return;

    this._galleryAbortController?.abort();
    this._resizeObserver?.disconnect();

    const slides = this._slides;
    const gallery = slides?.closest('.product-card__gallery');
    const galleryImages = slides?.querySelectorAll(
      '.product-card__gallery-image'
    );

    galleryImages?.forEach((image, index) => {
      if (index === 0) {
        image.classList.remove('product-card__gallery-image');
      } else {
        image.remove();
      }
    });

    if (slides) {
      slides.scrollLeft = 0;
      slides.classList.remove('product-card__gallery-slides', 'is-dragging');
      if (this._generatedSlidesId) slides.removeAttribute('id');
    }

    if (gallery && slides) {
      gallery.before(slides);
      gallery.remove();
    }
    this._scrollbar?.remove();

    this._slides = null;
    this._scrollbar = null;
    this._thumb = null;
    this._resizeObserver = null;
    this._galleryAbortController = null;
    this._generatedSlidesId = false;
    this._galleryBuilt = false;
  }

  // Generic pointer drag helper. Returns a function reporting whether the last
  // gesture actually moved (used to suppress click navigation).
  _enableDrag(handle, { threshold = 0, onStart, onMove, onEnd, signal }) {
    let pointerId = null;
    let startX = 0;
    let active = false;
    let moved = false;

    handle.addEventListener(
      'pointerdown',
      event => {
        // ignore touch events
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        moved = false;
        active = threshold === 0;
        onStart?.();
        if (active) {
          handle.setPointerCapture?.(pointerId);
          event.preventDefault();
        }
      },
      { signal }
    );

    handle.addEventListener(
      'pointermove',
      event => {
        if (event.pointerId !== pointerId) return;
        const dx = event.clientX - startX;
        if (!active && Math.abs(dx) > threshold) {
          active = true;
          handle.setPointerCapture?.(pointerId);
        }
        if (!active) return;
        moved = true;
        event.preventDefault();
        onMove(dx);
      },
      { signal }
    );

    const end = event => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (active) onEnd?.();
      active = false;
    };
    handle.addEventListener('pointerup', end, { signal });
    handle.addEventListener('pointercancel', end, { signal });
    handle.addEventListener('lostpointercapture', end, { signal });

    return () => moved;
  }

  _range() {
    return Math.max(this._slides.scrollWidth - this._slides.clientWidth, 0);
  }

  _snapToClosest() {
    const index = Math.round(
      this._slides.scrollLeft / this._slides.clientWidth
    );
    this._slides.scrollTo({
      left: index * this._slides.clientWidth,
      behavior: 'smooth'
    });
  }

  _updateScrollbar() {
    if (!this._slides || !this._scrollbar || !this._thumb) return;

    const track = this._scrollbar?.clientWidth;
    const range = this._range();
    if (!track || !range) return;

    const thumbWidth = Math.max(
      track * (this._slides.clientWidth / this._slides.scrollWidth),
      24
    );
    const progress = Math.min(Math.max(this._slides.scrollLeft / range, 0), 1);
    const imageNumber = Math.round(progress) + 1;

    this._thumb.style.width = `${thumbWidth}px`;
    this._thumb.style.transform = `translateX(${
      progress * (track - thumbWidth)
    }px)`;
    this._scrollbar.setAttribute('aria-valuenow', imageNumber);
    this._scrollbar.setAttribute('aria-valuetext', `Image ${imageNumber} of 2`);
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

class ShowMore extends HTMLElement {
  constructor() {
    super();
    this.content = null;
    this.button = null;
    this.expanded = false;
  }

  connectedCallback() {
    this.content = this.querySelector('.about-the-store__content');
    this.button = this.querySelector('.about-the-store__button');
    if (!this.content || !this.button) return;

    this.button.addEventListener('click', () => this._toggle());
    this._update();
  }

  _toggle() {
    this.expanded = !this.expanded;
    this._update();
  }

  _update() {
    if (this.expanded) {
      this.content.classList.add('about-the-store__content--expanded');
      this.button.textContent = 'See Less';
    } else {
      this.content.classList.remove('about-the-store__content--expanded');
      this.button.textContent = 'See More';
    }
  }
}

if (!customElements.get('show-more')) {
  customElements.define('show-more', ShowMore);
}
